import React, { Children, cloneElement, useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  Box,
  Badge,
  Popover,
  Typography,
  Divider,
  List,
  ListSubheader,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tooltip,
} from '@mui/material';
import { ListItem } from '@mui/material';
import { RaRecord, ReferenceField, useRecordContext } from 'react-admin';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MessageIcon from '@mui/icons-material/Message';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PrivacyTipIcon from '@mui/icons-material/Security';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import InboxIcon from '@mui/icons-material/Inbox';
import { useDataProvider, useRedirect } from 'react-admin';
import moment from 'moment';
import { Button } from '@mui/material';
import { get } from 'lodash'
import { useStore } from '../../hooks/useStore';
import { theme } from '../../theme';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { __t } from '../../i18n/t';
import { ATTENDANT_GOAL_TRIAL_ROUTE } from '../AttendantGoalOfferCard/offerCopy';

const PRIMARY_COLOR = theme.palette.primary.main;

// Meta de Frentista com IA (feature 161). Tipos = enum InboxNotificationType do backend.
// Só chaves técnicas ficam em constante; o texto sai de attendantGoalCopy(), chamada no render,
// porque o idioma troca em runtime sem reload.
const ATTENDANT_GOAL_ICON: Record<string, React.ReactElement> = {
  attendant_goal_offer: <TrackChangesIcon />,
  attendant_goal_weekly: <TrackChangesIcon />,
  attendant_goal_ending: <HourglassBottomIcon />,
  attendant_goal_ended: <TaskAltIcon />,
};

const attendantGoalCopy = (type: string): { title: string; text: string } | null => {
  switch (type) {
    case 'attendant_goal_offer':
      return { title: __t('Meta de Frentista pronta para sua equipe'), text: __t('Montamos uma meta com os dados dos seus postos. Veja e ative com um clique.') };
    case 'attendant_goal_weekly':
      return { title: __t('Leitura da semana pronta'), text: __t('A IA leu a semana da sua degustação de Meta de Frentista.') };
    case 'attendant_goal_ending':
      return { title: __t('Sua degustação está acabando'), text: __t('Faltam 10 dias. Veja os resultados até agora e peça para continuar.') };
    case 'attendant_goal_ended':
      return { title: __t('Sua degustação terminou'), text: __t('Veja como ficou a equipe antes e durante a Meta de Frentista.') };
    default:
      return null;
  }
};

const TitleField = (props) => {
  const { className, source, emptyText, ...rest } = props;
  const record = useRecordContext(props);
  const value = get(record, source);

  return (
    <Typography variant="subtitle2" gutterBottom>{record.type === 'support_ticket' ? `"${value}"` : value}</Typography>
  );
}

const NotificationItem = (props) => {
  const notification = props.record;
  if (!notification) { return <></>; }

  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const Icon = {
    'support_ticket': <MessageIcon />,
    'fraud': <PrivacyTipIcon />,
    ...ATTENDANT_GOAL_ICON,
  };

  const goalCopy = attendantGoalCopy(notification.type);

  const titleLabel = {
    'support_ticket': 'Mensagem de suporte',
    'fraud': 'Analise de segurança'
  };

  const updateToRead = async (notification: any) => {
    if (notification && notification.isRead === false) {
      await dataProvider.update('chains/chainId/inbox-notifications', { id: notification.id, data: { isRead: true }, previousData: notification });
      props.refreshNotifications();
    }
  }

  const handleItemClick = (notification: any) => {
    if (notification.type === 'support_ticket') {
      redirect(`/chains/chainId/support-tickets/${notification.notificationId}/show`);
    } else if (notification.type === 'fraud') {
      redirect(`/chains/chainId/potencial-frauds/${notification.notificationId}/show`);
    } else if (goalCopy) {
      redirect(ATTENDANT_GOAL_TRIAL_ROUTE);
    }
    updateToRead(notification);
    props.handleClose();
  }

  const SimpleShow = (props) => {
    const { record, children, resource, basePath } = props
    return (
      <>
        {
          Children.map(children, field =>
            cloneElement(field, {
              record,
              resource,
              basePath,
            })
          )
        }
      </>
    );
  }

  return (
    <ListItem
      button
      onClick={() => handleItemClick(notification)}
      selected={notification.isRead === false}
    >
      <ListItemAvatar style={{ minWidth: 45 }}>
        <Avatar sx={{
          height: '40px!important',
          width: '40px!important',
          marginLeft: '0px!important',
        }}>
          {Icon[notification.type]}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={goalCopy ? goalCopy.title : titleLabel[notification.type]}
        secondary={
          <>
            {
              notification.type === 'fraud' &&
              <SimpleShow record={notification}>
                <ReferenceField reference="chains/chainId/potencial-frauds" source="notificationId" label=" " link={false}>
                  <TitleField source="fraud.label" />
                </ReferenceField>
              </SimpleShow>
            }
            {
              notification.type === 'support_ticket' &&
              <SimpleShow record={notification}>
                <ReferenceField reference="chains/chainId/support-tickets" source="notificationId" label=" " link={false}>
                  <TitleField source="message" />
                </ReferenceField>
              </SimpleShow>
            }
            {goalCopy && (
              <Typography variant="body2" component="span" sx={{ display: 'block', mb: 0.5 }}>{goalCopy.text}</Typography>
            )}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              height: 15,
            }}>
              <AccessTimeIcon style={{ fontSize: 14, marginRight: 2 }} />
              <Typography style={{ lineHeight: '14px' }} variant="caption">{moment(notification.createdAt).fromNow()}</Typography>
            </Box>
          </>
        }
      />
    </ListItem>
  );
}

const NotificationPanel: React.FC = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const dataProvider = useDataProvider();

  const [unreadTotalSupportTicket, setUnreadTotalSupportTicket] = useStore('unreadTotalSupportTicket', 0);
  const [unreadTotalFraud, setUnreadTotalFraud] = useStore('unreadTotalFraud', 0);

  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationTotal, setNotificationTotal] = useState<number>(0);
  const [onlyRead, setOnlyRead] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);

  const PER_PAGE = 15;

  useEffect(() => {
    refreshApi();
  }, [onlyRead]);

  useEffect(() => {
    fetchInboxNotification();
  }, [page]);

  const fetchInboxNotification = () => {

    const filter: any = {};
    if (onlyRead) { filter.isRead = false; }

    dataProvider.getList('chains/chainId/inbox-notifications', { filter, pagination: { perPage: PER_PAGE, page }, sort: { field: 'createdAt', order: 'DESC' } }).then((response: any) => {
      setNotifications(notifications => page > 1 ? [...notifications, ...response.data] : response.data);
      setNotificationTotal(response.unreadTotal);
      setUnreadTotalSupportTicket(response.unreadTotalSupportTicket);
      setUnreadTotalFraud(response.unreadTotalFraud);

      setLastPage(Math.ceil(response.total / PER_PAGE));
    });
  };

  const refreshApi = useCallback(() => {
    if (page === 1) {
      fetchInboxNotification();
    } else {
      setPage(1);
      setLastPage(1);
    }
  }, [page, fetchInboxNotification]);

  const updateAllRead = async () => {
    dataProvider.update('chains/chainId/inbox-notifications/all-read', { id: '', data: { allRead: true } as RaRecord, previousData: {} as RaRecord })
      .finally(() => {
        refreshApi();
        handleClose();
      });
  }

  const EmptyView = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '15px', height: '300px' }}>
      <InboxIcon sx={{ marginBottom: 1, fontSize: 70, color: PRIMARY_COLOR }} />
      <Typography variant="subtitle1" gutterBottom>
        Nenhuma notificação recebida
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
      <IconButton
        color="inherit"
        onClick={handleClick}
      >
        <Badge badgeContent={notificationTotal} color="info">
          <NotificationsIcon />
        </Badge>
      </IconButton>


      <Popover
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            borderRadius: '8px',
            paddingBottom: '5px',
            width: '380px',
            maxWidth: '380px',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ padding: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Typography component="h6" variant="subtitle1" style={{ fontWeight: 'bold' }}>Notificações</Typography>
              <Typography component="p" variant="body2">{`Você tem ${notificationTotal} mensagens não lidas`}</Typography>
            </div>
            <Tooltip title={onlyRead ? 'Sem filtro' : 'Apenas não lidas'}>
              <IconButton onClick={() => setOnlyRead(!onlyRead)} sx={{ width: '35px', height: '35px', color: PRIMARY_COLOR }} color="primary" size="small">
                {
                  onlyRead ?
                    <FilterListOffIcon fontSize="small" />
                    :
                    <FilterListIcon fontSize="small" />
                }
              </IconButton>
            </Tooltip>
            <Tooltip title="Marcar todos como lidos">
              <IconButton
                onClick={updateAllRead}
                disabled={notificationTotal === 0}
                sx={{ width: '35px', height: '35px', color: PRIMARY_COLOR }}
                color="primary"
                size="small"
              >
                <DoneAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </Box>

        <Divider />

        <List
          sx={{ width: '100%', bgcolor: 'background.paper', overflow: 'auto', maxHeight: 550 }}
          component="nav"
          subheader={
            <ListSubheader style={{ lineHeight: '35px' }} component="div">
              Caixa de entrada
            </ListSubheader>
          }
        >
          {notifications.length === 0 && <EmptyView />}
          {
            notifications.map((notification, index) => (
              <NotificationItem key={index} record={notification} handleClose={handleClose} refreshNotifications={refreshApi} />
            ))
          }

          {
            page < lastPage &&
            <Box sx={{ padding: 2 }}>
              <Button
                onClick={() => setPage(page => page + 1)}
                color="primary"
                variant="outlined"
                fullWidth
              >
                Visualizar mais
              </Button>
            </Box>
          }
        </List>
      </Popover>
    </Box>
  );
}

export default NotificationPanel;
